'use client';

import { useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  AUTH_REDIRECT_KEY,
  FORCE_LOGIN_KEY,
  buildAccountsSignInUrl,
  safeRedirectTarget,
} from '@/lib/auth/client-constants';
import { ensureValidToken } from '@/lib/auth/client-refresh';
import { decodeJwtPayload, hashToken } from '@/lib/auth/decode-jwt';
import { installAuthAxiosInterceptor } from '@/lib/auth/install-axios-interceptor';
import { installWebviewAuthInterceptor } from '@/lib/auth/install-webview-auth-interceptor';
import type { AuthUser } from '@/lib/auth/types';
import {
  installWebinappAuthInterceptor,
  isWebinappSession,
  setWebinappUrlToken,
} from '@/lib/auth/webinapp-auth-interceptor';
import {
  clearWebinappToken,
  readWebinappToken,
} from '@/lib/auth/webinapp-token';
import { scheduleParamRemoval } from '@/lib/url-cleaner';
import {
  clearAuthCache,
  readAuthCache,
  useNewAuthStore,
  writeAuthCache,
} from '@/store/use-new-auth-store';

interface NewAuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (redirectTo?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const NewAuthContext = createContext<NewAuthContextValue | null>(null);

export function NewAuthProvider({ children }: { children: ReactNode }) {
  const user = useNewAuthStore.use.user();
  const isAuthenticated = useNewAuthStore.use.isAuthenticated();
  const isLoading = useNewAuthStore.use.isLoading();

  const setUser = useNewAuthStore.use.setUser();
  const setCustomerId = useNewAuthStore.use.setCustomerId();
  const setIsLoading = useNewAuthStore.use.setIsLoading();
  const resetStore = useNewAuthStore.use.logout();

  const searchParams = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const initRanRef = useRef(false);

  /* ---------- Hydrate from cookie + optimistic JWT decode ---------- */
  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;

    // WebInApp token: đọc trực tiếp từ window.location để không phụ thuộc thời
    // điểm hydrate của useSearchParams (đôi khi null ở render đầu → miss login).
    let urlToken = tokenFromUrl;
    if (!urlToken && typeof window !== 'undefined') {
      try {
        urlToken = new URLSearchParams(window.location.search).get('token');
      } catch {
        /* ignore */
      }
    }

    // Token URL ⟹ phiên webinapp DỨT KHOÁT (kể cả khi còn refresh_token cũ của
    // account web khác — token URL là token chính, takeover). isWebinappSession
    // xét thêm cookie/localStorage cho lần reload/điều hướng sau (không còn ?token=).
    const webinapp = Boolean(urlToken) || isWebinappSession(urlToken);

    try {
      // Chọn ĐÚNG 1 interceptor theo loại session — webinapp và web xử lý tách
      // biệt hoàn toàn (webinapp không có refresh_token nên không dùng refresh chain).
      if (webinapp) {
        // Cross-account takeover: khi vào app KÈM token URL, xóa refresh_token +
        // token cũ (có thể của account web khác) TRƯỚC khi cài interceptor → phiên
        // web cũ không hijack routing và không bị refresh chain "cứu" sai account.
        if (urlToken) clearTokenCookiesClientSide();
        // Persist token URL làm token chính NGAY (cookie + bền) để interceptor đọc
        // được kể cả khi request chạy trước hydrateAndSync trên webview chậm.
        setWebinappUrlToken(urlToken);
        installWebinappAuthInterceptor();
      } else {
        installAuthAxiosInterceptor();
        // Order/estimate đi qua webviewHttpClient (không qua publicHttpClient) nên
        // cần 401 → refresh/logout riêng, nếu không web user kẹt khi token hết hạn.
        installWebviewAuthInterceptor();
      }
      cleanupLegacyArtifacts();
    } catch (e) {
      console.error('[auth] init setup failed', e);
    }

    // Watchdog: never leave the UI stuck in "loading user" state. If hydrate
    // stalls for any reason (e.g. a hung refresh/sync request), force-unblock.
    const watchdog = setTimeout(() => setIsLoading(false), 12_000);

    void hydrateAndSync({
      isWebinapp: webinapp,
      tokenFromUrl: urlToken,
      setUser,
      setCustomerId,
      setIsLoading,
      resetStore,
    })
      .catch((err) => {
        // Any unexpected throw must still release the loading state.
        console.error('auth hydrate failed', err);
        setIsLoading(false);
      })
      .finally(() => clearTimeout(watchdog));
  }, [tokenFromUrl, setUser, setCustomerId, setIsLoading, resetStore]);

  /* ---------- Redirect-back sau đăng nhập ----------
   * login() lưu pathname hiện tại vào AUTH_REDIRECT_KEY trước khi bounce qua
   * accounts; callback luôn redirect về '/'. Khi auth xong, đưa user về ĐÚNG màn
   * họ đã bấm đăng nhập (vd tiếp tục thanh toán trong luồng order). Consume 1 lần. */
  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      const target = sessionStorage.getItem(AUTH_REDIRECT_KEY);
      if (!target) return;
      sessionStorage.removeItem(AUTH_REDIRECT_KEY);
      const current = window.location.pathname + window.location.search;
      if (
        target.startsWith('/') &&
        !target.startsWith('//') &&
        target !== current
      ) {
        window.location.replace(target);
      }
    } catch {
      /* ignore */
    }
  }, [isAuthenticated]);

  /* ---------- Login: OAuth Authorization Code Flow (doc §5.1) ----------
   * 1. Gen state random, save vào HttpOnly cookie via /api/auth/state
   * 2. Save redirect target vào sessionStorage (giữ qua bounce SSO)
   * 3. Redirect ra accounts với client_id + state
   * 4. Accounts mint code (encrypted), redirect về /auth/callback?code=&state=
   * 5. Callback route.ts (server) verify state + exchange code → token + set cookies */
  const login = useCallback(async (redirectTo?: string) => {
    try {
      const target = safeRedirectTarget(
        redirectTo ?? window.location.pathname + window.location.search
      );
      sessionStorage.setItem(AUTH_REDIRECT_KEY, target);

      // Sau khi user chủ động logout, ép accounts đăng xuất + hiện form ở lần
      // login kế tiếp (prompt=login) thay vì silent SSO bằng session cũ.
      let forceLogin = false;
      try {
        forceLogin = localStorage.getItem(FORCE_LOGIN_KEY) === '1';
      } catch {
        /* ignore */
      }

      const state =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);

      // Save state vào HttpOnly cookie qua server route (an toàn hơn sessionStorage)
      const stateRes = await fetch('/api/auth/state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state }),
      });
      if (!stateRes.ok) {
        console.error('login: state set failed');
        return;
      }

      const callback = `${window.location.origin}/auth/callback`;
      // Consume cờ ngay trước khi redirect (chỉ ép 1 lần sau logout).
      try {
        localStorage.removeItem(FORCE_LOGIN_KEY);
      } catch {
        /* ignore */
      }
      window.location.href = buildAccountsSignInUrl(
        callback,
        state,
        forceLogin ? { prompt: 'login' } : undefined
      );
    } catch (err) {
      console.error('login failed', err);
    }
  }, []);

  /* ---------- Logout: clear cookies (server + client) + reset stores + nav ----
   * Defense-in-depth: nếu server Set-Cookie bị browser drop (Secure flag mismatch,
   * SameSite quirks...) thì client-side cookie clear vẫn đảm bảo logout thành công.
   * Cũng remove zustand persist storage để tránh re-hydrate user sau hard nav.
   */
  const logout = useCallback(async () => {
    // Xoá session phía ahafood ở client (cookie .ahafood.ai đang JS-readable +
    // localStorage zustand + auth cache + webinapp token).
    clearTokenCookiesClientSide();
    clearWebinappToken();
    clearPersistedAuthStorage();
    resetStore();
    clearAuthCache();

    // KHÔNG redirect sang accounts. Đặt cờ để lần login kế tiếp gửi prompt=login
    // → accounts tự đăng xuất session .ahamove.com + hiện form (đã hỗ trợ phía
    // accounts) → tránh silent re-login bằng account cũ.
    try {
      localStorage.setItem(FORCE_LOGIN_KEY, '1');
    } catch {
      /* ignore */
    }

    // Clear cookie ahafood SERVER-SIDE (đáng tin nhất): Set-Cookie đúng
    // Domain=.ahafood.ai + host-only variant, KHÔNG phụ thuộc detect hostname.
    // Đây là fix chính cho prod apex `ahafood.ai` — client-side clear từng miss
    // cookie domain. Client-side clear ở trên giữ làm backup.
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch {
      /* ignore — client clear bên trên là backup */
    }

    // Về trang chủ (same-origin, KHÔNG phải redirect cross-domain) để UI về
    // trạng thái đã đăng xuất.
    window.location.replace('/');
  }, [resetStore]);

  const value = useMemo<NewAuthContextValue>(
    () => ({ user, isAuthenticated, isLoading, login, logout }),
    [user, isAuthenticated, isLoading, login, logout]
  );

  return (
    <NewAuthContext.Provider value={value}>{children}</NewAuthContext.Provider>
  );
}

export function useNewAuth() {
  const ctx = useContext(NewAuthContext);
  if (!ctx) throw new Error('useNewAuth must be used within NewAuthProvider');
  return ctx;
}

/* ---------------- Hydrate logic ---------------- */

interface HydrateOpts {
  isWebinapp: boolean;
  tokenFromUrl: string | null;
  setUser: (u: AuthUser | null) => void;
  setCustomerId: (id: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  resetStore: () => void;
}

/**
 * Token nguồn theo LOẠI PHIÊN (không theo việc có ?token= hay không — reload
 * webinapp không còn token trên URL nhưng VẪN là webinapp).
 *  - WebInApp: token CHÍNH = token URL (đã persist ở bootstrap) hoặc token bền đọc
 *    qua readWebinappToken (mem → localStorage → cookie). KHÔNG gọi ensureValidToken
 *    (không có refresh_token → refresh chain luôn 401 + xóa cookie → logout oan) và
 *    KHÔNG đọc cookie `token` nhập nhằng trực tiếp (có thể token cũ/cross-account).
 *  - Web thường: cookie + pre-emptive refresh nếu token sắp expire (<30s).
 */
async function resolveSessionToken(
  isWebinapp: boolean,
  tokenFromUrl: string | null
): Promise<string | null> {
  if (!isWebinapp) return ensureValidToken();
  if (tokenFromUrl) {
    try {
      clearTokenFromUrl();
    } catch {
      /* ignore — không chặn login nếu dọn URL lỗi */
    }
    return tokenFromUrl;
  }
  const stored = readWebinappToken();
  return stored ? stored : null;
}

async function hydrateAndSync({
  isWebinapp,
  tokenFromUrl,
  setUser,
  setCustomerId,
  setIsLoading,
  resetStore,
}: HydrateOpts) {
  // 1. Chọn token nguồn theo loại phiên.
  const token = await resolveSessionToken(isWebinapp, tokenFromUrl);

  if (!token) {
    // Cookie missing (user xóa tay, hoặc refresh fail) — clear stale persisted
    // state để UI hiện "Chưa đăng nhập" đúng.
    resetStore();
    clearAuthCache();
    setIsLoading(false);
    return;
  }

  // 3. Optimistic — decode JWT, set user state ngay
  const claims = decodeJwtPayload(token);
  if (!claims) {
    console.error('[auth] token decode failed (malformed JWT)');
    resetStore();
    clearAuthCache();
    setIsLoading(false);
    return;
  }

  // customerId = JWT cid (số điện thoại format 84xxxxxxxxx) — không cần BE sync.
  // JWT là source of truth, available NGAY từ first paint.
  const customerId = claims.cid;

  setUser({
    id: claims.sub,
    phone: claims.cid,
    phoneNumber: claims.cid,
    name: claims.noc,
    email: claims.eoc,
    provider: 'ahamove',
    loginType: 'ahamove',
    systemToken: token,
    cunstomerId: customerId,
  });
  setCustomerId(customerId);
  setIsLoading(false);

  // 4. Sync — đăng ký session với BE. QUAN TRỌNG: token webinapp chỉ "hoạt động"
  //    (BE nhận diện cho các API call) SAU khi login/sync API này được gọi.
  //    - WebInApp: LUÔN gọi sync, bỏ qua cache, và truyền token CHÍNH thẳng trong
  //      body (không phụ thuộc cookie nhập nhằng). KHÔNG dùng token trả về từ login
  //      API — sync chỉ để kích hoạt phiên (sync.ts chỉ đọc customer_id/ahamove_id).
  //    - Web thường: skip nếu đã sync token này gần đây (hash match cache).
  const cache = readAuthCache();
  const currentHash = await hashToken(token);
  if (!isWebinapp && cache && cache.tokenHash === currentHash) {
    return;
  }

  try {
    const syncController = new AbortController();
    const syncTimeout = setTimeout(() => syncController.abort(), 10_000);
    const res = await fetch('/api/auth/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      signal: syncController.signal,
    }).finally(() => clearTimeout(syncTimeout));
    if (res.status === 401) {
      if (isWebinapp) {
        // WebInApp: 401 ở bootstrap sync THƯỜNG là transient — token chưa kịp
        // activate phía BE (race trên thiết bị/webview chậm = đúng nguyên nhân
        // "chỉ lỗi vài máy"). KHÔNG xoá session: giữ optimistic state, để
        // installWebinappAuthInterceptor re-sync + retry ở API call kế. Token
        // thật sự hỏng → call lỗi nhưng KHÔNG logout (đúng lựa chọn "giữ phiên").
        return;
      }
      resetStore();
      setIsLoading(false);
      clearAuthCache();
      await fetch('/api/auth/logout', { method: 'POST' });
      return;
    }
    if (!res.ok) return; // 5xx → keep state, retry next load

    writeAuthCache({
      customerId,
      tokenHash: currentHash,
      syncedAt: Date.now(),
    });
  } catch {
    /* network fail → keep state */
  }
}

/**
 * Xóa cookies + localStorage cũ của old auth flow.
 * Chạy 1 lần khi mount NewAuthProvider — đảm bảo no leftover state.
 */
/** Client-side: detect deployed env qua hostname (NEXT_PUBLIC_APP_ENV không có sẵn). */
function isAhafoodDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  // Match cả apex (ahafood.ai) lẫn subdomain (stg./uat.ahafood.ai). Trước đây
  // chỉ endsWith('.ahafood.ai') → apex prod `ahafood.ai` bị MISS (không có dấu
  // chấm đầu) → không xoá được cookie Domain=.ahafood.ai khi logout.
  return host === 'ahafood.ai' || host.endsWith('.ahafood.ai');
}

function cleanupLegacyArtifacts() {
  try {
    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `ws_token=; path=/; ${expire}`;
    if (isAhafoodDomain()) {
      document.cookie = `ws_token=; path=/; domain=.ahafood.ai; ${expire}`;
    }

    if (localStorage.getItem('auth.store')) {
      localStorage.removeItem('auth.store');
    }
  } catch {
    /* ignore */
  }
}

function clearTokenFromUrl() {
  scheduleParamRemoval('token');
}

/**
 * Client-side cookie clear — chạy mọi variant (host-only + apex domain, có/không
 * Secure). Đảm bảo cookie bị xóa kể cả khi server Set-Cookie response bị browser
 * drop do mismatch SameSite/Secure attributes.
 */
function clearTokenCookiesClientSide() {
  if (typeof document === 'undefined') return;
  const names = ['token', 'refresh_token', 'auth_state'];
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  for (const name of names) {
    document.cookie = `${name}=; path=/; ${expire}`;
    if (isAhafoodDomain()) {
      document.cookie = `${name}=; path=/; domain=.ahafood.ai; ${expire}`;
    }
  }
}

/**
 * Xóa zustand persist storage của auth store. Tránh case sau hard nav, persist
 * middleware hydrate lại user cũ từ localStorage trước khi hydrateAndSync chạy.
 */
function clearPersistedAuthStorage() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('ahafood.new-auth');
  } catch {
    /* ignore */
  }
}
